import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError, fmt, fmtPiles, fmtDateTime } from "@/lib/api";
import { OPS, MOTIFS, linesFromMap, mapFromLines } from "@/lib/ops";
import QtyLinesEditor from "@/components/QtyLinesEditor";
import { CheckCircle2, AlertTriangle, Truck, ArrowLeft } from "lucide-react";

function maxFor(matrix, site, state) {
  const m = {};
  matrix.filter((r) => r.site === site).forEach((r) => (m[r.packaging_type] = r[state]));
  return m;
}

export default function OperationDialog({ type, open, onOpenChange, onDone, matrix, types, sites, user, editMovement }) {
  const op = OPS[type] || {};
  const isEdit = !!editMovement;

  const [lines, setLines] = useState({});
  const [lavees, setLavees] = useState({});
  const [rebutees, setRebutees] = useState({});
  const [chauffeur, setChauffeur] = useState("");
  const [camion, setCamion] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [motif, setMotif] = useState(MOTIFS[0]);
  const [perteState, setPerteState] = useState("propre");
  const [perteSite, setPerteSite] = useState(user?.site || "ANNEZIN");
  const [destination, setDestination] = useState("");
  const [surplusConfirm, setSurplusConfirm] = useState(false);
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ecart, setEcart] = useState(false);
  const [saving, setSaving] = useState(false);

  const isReception = type === "RECEPTION_SALES" || type === "RECEPTION_PROPRES";
  // Tout se saisit en piles, sauf les casses (PERTE) et les rebuts, en unités.
  const isPile = type !== "PERTE";
  const pileSizeMap = useMemo(
    () => Object.fromEntries(types.map((t) => [t.code, t.pile_size || 1])),
    [types]
  );
  // Piles -> unités ; une quantité d'origine non modifiée est renvoyée telle quelle (pas d'arrondi).
  const toUnits = (map, orig) => {
    const o = {};
    (orig || []).forEach((l) => { o[l.packaging_type] = (o[l.packaging_type] || 0) + l.quantity; });
    return linesFromMap(map).map((l) => {
      const ps = pileSizeMap[l.packaging_type] || 1;
      const ou = o[l.packaging_type];
      const keep = ou != null && Math.round(ou / ps) === l.quantity;
      return { packaging_type: l.packaging_type, quantity: keep ? ou : l.quantity * ps };
    });
  };
  const editOrig = (k) => (isEdit ? editMovement?.[k] : null);
  const pileMax = (site, state) =>
    Object.fromEntries(Object.entries(maxFor(matrix, site, state)).map(([k, v]) => [k, Math.floor(v / (pileSizeMap[k] || 1))]));
  const unitsToPileMap = (lns) => {
    const m = {};
    (lns || []).forEach((l) => {
      m[l.packaging_type] = Math.round(l.quantity / (pileSizeMap[l.packaging_type] || 1));
    });
    return m;
  };

  useEffect(() => {
    if (!open) return;
    // reset
    setChauffeur(""); setCamion(""); setCommentaire(""); setEcart(false); setSelected(null); setSurplusConfirm(false);
    setMotif(MOTIFS[0]); setPerteState("propre"); setPerteSite(user?.site || "ANNEZIN");
    setDestination("");
    if (isEdit) {
      setLines(isPile ? unitsToPileMap(editMovement.lines) : mapFromLines(editMovement.lines));
      setLavees(unitsToPileMap(editMovement.lines_lavees));
      setRebutees(mapFromLines(editMovement.lines_rebutees));
      setCommentaire(editMovement.commentaire || "");
      setMotif(editMovement.motif || MOTIFS[0]);
      setPerteState(editMovement.state || "propre");
      setPerteSite(editMovement.site || user?.site || "ANNEZIN");
      setDestination(editMovement.to_site || "");
      setChauffeur(editMovement.chauffeur || "");
      setCamion(editMovement.camion || "");
    } else {
      setLines({}); setLavees({}); setRebutees({});
    }
    if (isReception && !isEdit) {
      const kind = type === "RECEPTION_SALES" ? "sales" : "propres";
      api.get(`/movements/pending?kind=${kind}`).then(({ data }) => setPending(data)).catch(() => setPending([]));
    }
    if (isReception && isEdit && editMovement.ref_id) {
      const src = type === "RECEPTION_SALES" ? "ENVOI_SALES" : "REPARTITION";
      setEcart(true);
      setSurplusConfirm(!!editMovement.surplus_confirm);
      api.get(`/movements?type=${src}`)
        .then(({ data }) => setSelected(data.find((x) => x.id === editMovement.ref_id) || null))
        .catch(() => setSelected(null));
    }
  }, [open]); // eslint-disable-line

  const destSites = useMemo(() => sites.filter((s) => ["production", "destination"].includes(s.kind)), [sites]);
  const receivedUnits = (code) => {
    const v = Number(lines[code]) || 0;
    if (!isPile) return v;
    const sent = (selected?.lines || []).filter((l) => l.packaging_type === code).reduce((n, l) => n + l.quantity, 0);
    const ps = pileSizeMap[code] || 1;
    return sent && Math.round(sent / ps) === v ? sent : v * ps;
  };
  const sentMap = useMemo(() => {
    const m = {};
    (selected?.lines || []).forEach((l) => { m[l.packaging_type] = (m[l.packaging_type] || 0) + l.quantity; });
    return m;
  }, [selected]);
  const surplusTypes = selected ? types.filter((t) => receivedUnits(t.code) > (sentMap[t.code] || 0)) : [];
  const lossTypes = selected ? types.filter((t) => (sentMap[t.code] || 0) > receivedUnits(t.code)) : [];
  const commentMissing = isReception && ecart && !commentaire.trim();
  const surplusBlocked = isReception && surplusTypes.length > 0 && !surplusConfirm;
  const backToList = () => { setSelected(null); setEcart(false); setLines({}); setSurplusConfirm(false); setCommentaire(""); };

  const selectPending = (id) => {
    const p = pending.find((x) => x.id === id);
    setSelected(p);
    setEcart(false);
    setLines(isPile ? unitsToPileMap(p?.lines) : mapFromLines(p?.lines));
  };

  const submit = async () => {
    let body = { type };
    if (type === "ENVOI_SALES") {
      body = { ...body, from_site: "ANNEZIN", to_site: "DDO", lines: toUnits(lines, editOrig("lines")), chauffeur, camion, commentaire };
    } else if (type === "LAVAGE") {
      body = { ...body, site: "DDO", lines_lavees: toUnits(lavees, editOrig("lines_lavees")), lines_rebutees: linesFromMap(rebutees), commentaire };
    } else if (type === "LAVAGE_START") {
      body = { ...body, site: "DDO", lines: toUnits(lines, editOrig("lines")), commentaire };
    } else if (type === "LAVAGE_END") {
      body = { ...body, site: "DDO", lines_lavees: toUnits(lavees, editOrig("lines_lavees")), lines_rebutees: linesFromMap(rebutees), commentaire };
    } else if (type === "REPARTITION") {
      if (!destination) { toast.error("Choisissez le site de destination."); return; }
      body = { ...body, from_site: "DDO", to_site: destination, lines: toUnits(lines, editOrig("lines")), commentaire };
    } else if (type === "PERTE") {
      body = { ...body, site: perteSite, state: perteState, motif, lines: linesFromMap(lines), commentaire };
    } else if (isReception) {
      if (!selected) { toast.error("Sélectionnez un envoi à réceptionner."); return; }
      if (commentMissing) { toast.error("Le commentaire est obligatoire pour signaler un écart."); return; }
      if (surplusBlocked) { toast.error("Confirmez la quantité reçue supérieure à l'envoi."); return; }
      body = {
        ...body, from_site: selected.from_site, to_site: selected.to_site,
        ref_id: selected.id, lines: isPile ? toUnits(lines, isEdit ? editMovement.lines : selected.lines) : linesFromMap(lines), ecart, commentaire,
        surplus_confirm: surplusConfirm,
      };
    }
    setSaving(true);
    try {
      let created = null;
      if (isEdit) await api.put(`/movements/${editMovement.id}`, { ...body, type });
      else created = (await api.post("/movements", body)).data;
      toast.success(isEdit ? "Saisie modifiée." : "Saisie enregistrée.");
      if (!isEdit && type === "ENVOI_SALES" && created?.id) {
        try {
          const res = await api.get(`/movements/${created.id}/bordereau.pdf`, { responseType: "blob" });
          const url = URL.createObjectURL(res.data);
          const a = document.createElement("a");
          a.href = url;
          a.download = `bordereau_${created.id.slice(0, 8)}.pdf`;
          a.click();
          URL.revokeObjectURL(url);
          toast.success("Bordereau d'envoi généré.");
        } catch { /* non bloquant */ }
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`operation-dialog-${type}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? "Modifier — " : ""}{op.label}</DialogTitle>
          <DialogDescription>{op.desc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {type === "ENVOI_SALES" && (
            <>
              <QtyLinesEditor types={types} value={lines} onChange={setLines} accent="amber" pileMode
                maxMap={pileMax("ANNEZIN", "sale")} testid="envoi" />
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Chauffeur</Label><Input value={chauffeur} onChange={(e) => setChauffeur(e.target.value)} data-testid="envoi-chauffeur" /></div>
                <div><Label>N° camion</Label><Input value={camion} onChange={(e) => setCamion(e.target.value)} data-testid="envoi-camion" /></div>
              </div>
            </>
          )}

          {type === "LAVAGE" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="font-display font-bold text-green-800 mb-2 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Piles lavées (propres)</p>
                <QtyLinesEditor types={types} value={lavees} onChange={setLavees} accent="green" pileMode
                  maxMap={pileMax("DDO", "sale")} testid="lave" />
              </div>
              <div>
                <p className="font-display font-bold text-red-800 mb-2 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Rebuts / cassés (en unités)</p>
                <QtyLinesEditor types={types} value={rebutees} onChange={setRebutees} accent="red" testid="rebut" />
              </div>
            </div>
          )}

          {type === "LAVAGE_START" && (
            <>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
                Déplacez les emballages <strong>sales en attente</strong> vers l'état <strong>« en cours de lavage »</strong>.
              </div>
              <QtyLinesEditor types={types} value={lines} onChange={setLines} accent="amber" pileMode
                maxMap={pileMax("DDO", "sale")} testid="lavage-start" />
            </>
          )}

          {type === "LAVAGE_END" && (
            <>
              <div className="rounded-lg bg-green-50 border border-green-200 p-2.5 text-xs text-green-800">
                Finalisez le lavage : les emballages <strong>en cours de lavage</strong> deviennent <strong>propres</strong> (ou rebutés si cassés).
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="font-display font-bold text-green-800 mb-2 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Piles lavées (propres)</p>
                  <QtyLinesEditor types={types} value={lavees} onChange={setLavees} accent="green" pileMode
                    maxMap={pileMax("DDO", "en_lavage")} testid="lave-end" />
                </div>
                <div>
                  <p className="font-display font-bold text-red-800 mb-2 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Rebuts / cassés (en unités)</p>
                  <QtyLinesEditor types={types} value={rebutees} onChange={setRebutees} accent="red" testid="rebut-end" />
                </div>
              </div>
            </>
          )}

          {type === "REPARTITION" && (
            <>
              <div className="rounded-lg bg-green-50 border border-green-200 p-2.5 text-xs text-green-800">
                L'enlèvement des propres chez DDO se compte en <strong>piles</strong> (1 pile de palette = 11 palettes, 1 pile d'intercalaire = 50 intercalaires, 1 pile d'alvéole = 2160 alvéoles).
              </div>
              <div>
                <Label>Site de destination *</Label>
                <Select value={destination} onValueChange={setDestination}>
                  <SelectTrigger className={`h-12 ${destination ? "" : "border-amber-400"}`} data-testid="repartition-destination"><SelectValue placeholder="Choisir un site de production…" /></SelectTrigger>
                  <SelectContent>
                    {destSites.map((s) => <SelectItem key={s.code} value={s.code} data-testid={`destination-option-${s.code}`}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <QtyLinesEditor types={types} value={lines} onChange={setLines} accent="green" pileMode
                maxMap={pileMax("DDO", "propre")}
                testid="repartition" />
            </>
          )}

          {type === "PERTE" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Site</Label>
                  <Select value={perteSite} onValueChange={(v) => { setPerteSite(v); if (v !== "DDO" && perteState === "en_lavage") setPerteState("propre"); }} disabled={user?.role !== "admin"}>
                    <SelectTrigger className="h-12" data-testid="perte-site"><SelectValue /></SelectTrigger>
                    <SelectContent>{sites.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>État</Label>
                  <Select value={perteState} onValueChange={setPerteState}>
                    <SelectTrigger className="h-12" data-testid="perte-state"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="propre">Propre</SelectItem>
                      <SelectItem value="sale">Sale</SelectItem>
                      {perteSite === "DDO" && <SelectItem value="en_lavage" data-testid="perte-state-en-lavage">En cours de lavage</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Motif</Label>
                <Select value={motif} onValueChange={setMotif}>
                  <SelectTrigger className="h-12" data-testid="perte-motif"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOTIFS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <QtyLinesEditor types={types} value={lines} onChange={setLines} accent="red"
                maxMap={maxFor(matrix, perteSite, perteState)} testid="perte" />
            </>
          )}

          {isReception && (
            <>
              {!isEdit && pending.length === 0 && <p className="text-slate-500 text-sm py-4 text-center">Aucun envoi en attente de réception.</p>}
              {!isEdit && pending.length > 0 && !selected && (
                <div className="space-y-2">
                  <Label>Envois en transit</Label>
                  {pending.map((p) => (
                    <button key={p.id} onClick={() => selectPending(p.id)}
                      className="tactile w-full text-left rounded-xl border-2 border-slate-200 hover:border-blue-400 bg-white p-3"
                      data-testid={`pending-item-${p.id}`}>
                      <div className="flex items-center gap-2 font-display font-bold text-slate-800">
                        <Truck className="h-4 w-4 text-blue-700" />
                        {p.from_site} → {p.to_site}
                        <span className="text-xs font-normal text-slate-500 ml-auto">{fmtDateTime(p.date, { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
                      </div>
                      <div className="text-sm text-slate-600 mt-1 font-data">
                        {(p.lines || []).map((l) => isPile
                          ? `${l.packaging_type}: ${fmtPiles(l.quantity, pileSizeMap[l.packaging_type], true)}`
                          : `${l.packaging_type}: ${fmt(l.quantity)}`).join(" · ")}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {selected && (
                <>
                  {!isEdit && (
                    <button type="button" onClick={backToList} data-testid="reception-back-button"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:underline">
                      <ArrowLeft className="h-4 w-4" /> Retour aux envois en transit
                    </button>
                  )}
                  <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-3">
                    <div className="text-xs uppercase font-bold text-blue-700 mb-1">Quantités annoncées{isPile ? " (piles)" : ""}</div>
                    <div className="font-data font-bold text-blue-900">
                      {(selected.lines || []).map((l) => isPile
                        ? `${l.packaging_type}: ${fmtPiles(l.quantity, pileSizeMap[l.packaging_type], true)}`
                        : `${l.packaging_type}: ${fmt(l.quantity)}`).join(" · ")}
                    </div>
                  </div>
                  {!ecart ? (
                    <div className="grid grid-cols-1 gap-2">
                      <Button className="h-14 bg-green-600 hover:bg-green-700 text-white font-bold text-base tactile"
                        onClick={submit} disabled={saving} data-testid="contradictory-conform-button">
                        <CheckCircle2 className="h-5 w-5 mr-2" /> Conforme au bordereau
                      </Button>
                      <Button variant="outline" className="h-14 border-2 border-amber-400 text-amber-800 font-bold text-base tactile"
                        onClick={() => setEcart(true)} data-testid="contradictory-discrepancy-button">
                        <AlertTriangle className="h-5 w-5 mr-2" /> Signaler un écart
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="font-display font-bold text-amber-800">Quantités réellement reçues{isPile ? " (piles)" : ""}</p>
                      <QtyLinesEditor types={types} value={lines} onChange={setLines} accent="amber" pileMode={isPile} testid="reception" />
                      {lossTypes.length > 0 && (
                        <div className="rounded-lg bg-red-50 border border-red-200 p-2.5 text-sm text-red-800" data-testid="reception-loss-preview">
                          <strong>Perte transport enregistrée automatiquement :</strong>{" "}
                          {lossTypes.map((t) => `${t.label} ${fmt((sentMap[t.code] || 0) - receivedUnits(t.code))} u.`).join(" · ")}
                        </div>
                      )}
                      {surplusTypes.length > 0 && (
                        <label className="flex items-start gap-2 rounded-lg bg-amber-50 border-2 border-amber-300 p-2.5 text-sm text-amber-900 cursor-pointer" data-testid="reception-surplus-box">
                          <input type="checkbox" className="mt-1 h-4 w-4" checked={surplusConfirm}
                            onChange={(e) => setSurplusConfirm(e.target.checked)} data-testid="reception-surplus-confirm" />
                          <span>
                            Quantité reçue <strong>supérieure à l'envoi</strong> ({surplusTypes.map((t) => t.label).join(", ")}).
                            Je confirme cette quantité et j'indique le motif ci-dessous.
                          </span>
                        </label>
                      )}
                      <div>
                        <Label>Commentaire / motif de l'écart (obligatoire)</Label>
                        <Textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} data-testid="reception-comment"
                          className={commentMissing ? "border-red-400" : ""} placeholder="Ex. : 1 pile manquante à l'arrivée du camion" />
                        {commentMissing && <p className="text-xs text-red-600 mt-1" data-testid="reception-comment-error">Le commentaire est obligatoire pour signaler un écart.</p>}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {!isReception && (
            <div>
              <Label>Commentaire</Label>
              <Textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} data-testid="op-comment" />
            </div>
          )}
        </div>

        {/* footer submit for non conforme-reception flows */}
        {!(isReception && selected && !ecart && !isEdit) && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="h-12">Annuler</Button>
            <Button onClick={submit} disabled={saving || (isReception && (!selected || commentMissing || surplusBlocked)) || (type === "REPARTITION" && !destination)}
              className="h-12 bg-blue-800 hover:bg-blue-900 text-white font-bold text-base tactile"
              data-testid="operation-submit-button">
              {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Valider"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
